/**
 * Ordering, stacking and geometry for bar charts.
 */

import type { BandScale, Scale } from "@/lib/tool-kit/charts/scale";

export type SortOrder = "none" | "ascending" | "descending";

/**
 * Row indices in display order: as entered, or by `keys`. Stable, and a row whose key
 * is missing (NaN) always goes last, whichever direction is chosen.
 */
export function sortIndices(
  keys: readonly number[],
  order: SortOrder
): number[] {
  const indices = keys.map((_, index) => index);
  if (order === "none") {
    return indices;
  }
  const direction = order === "ascending" ? 1 : -1;
  return indices.sort((a, b) => {
    const ka = keys[a];
    const kb = keys[b];
    const aMissing = !Number.isFinite(ka);
    const bMissing = !Number.isFinite(kb);
    if (aMissing || bMissing) {
      return Number(aMissing) - Number(bMissing) || a - b;
    }
    return (ka - kb) * direction || a - b;
  });
}

/** The sum of a row's numbers, ignoring missing cells. */
export function rowTotal(values: readonly (number | null)[]): number {
  let total = 0;
  for (const value of values) {
    total += value ?? 0;
  }
  return total;
}

export interface StackSegment {
  series: number;
  value: number;
  /** Where the segment starts and ends on the value axis. */
  start: number;
  end: number;
}

/**
 * Stack one category's values: positive values upward from zero and negative values
 * downward, each in series order — the convention that keeps a stacked bar with mixed
 * signs readable instead of letting segments overlap.
 */
export function stackRow(values: readonly (number | null)[]): StackSegment[] {
  let above = 0;
  let below = 0;
  const segments: StackSegment[] = [];
  for (const [series, value] of values.entries()) {
    if (value === null || value === 0) {
      continue;
    }
    if (value > 0) {
      segments.push({ series, value, start: above, end: above + value });
      above += value;
    } else {
      segments.push({ series, value, start: below, end: below + value });
      below += value;
    }
  }
  return segments;
}

/**
 * The value range a set of bars needs, always including zero — a bar's length is its
 * value, so an axis that does not start at zero misstates every bar on it.
 */
export function barExtent(
  rows: readonly (readonly (number | null)[])[],
  stacked: boolean
): [number, number] {
  let min = 0;
  let max = 0;
  for (const values of rows) {
    if (stacked) {
      for (const segment of stackRow(values)) {
        min = Math.min(min, segment.end);
        max = Math.max(max, segment.end);
      }
      continue;
    }
    for (const value of values) {
      if (value !== null) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }
  }
  return [min, max];
}

export type BarMode = "grouped" | "stacked";
export type BarOrientation = "vertical" | "horizontal";

export interface BarRect {
  /** Position of the category in display order. */
  row: number;
  series: number;
  value: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BarLayoutInput {
  /** Values per category, in display order; one entry per series. */
  rows: readonly (readonly (number | null)[])[];
  mode: BarMode;
  orientation: BarOrientation;
  /** Maps a value onto the value axis (y for vertical bars, x for horizontal). */
  valueScale: Scale;
  /** Slots along the category axis. */
  band: BandScale;
  /** Widest a single bar may be; a wide slot centres a capped bar. */
  maxThickness: number;
  /** Space between neighbouring bars of one group. */
  groupGap?: number;
}

/** Turn a run along the value axis and a run along the category axis into a rect. */
function placeBar(
  orientation: BarOrientation,
  along: [number, number],
  across: [number, number]
): Pick<BarRect, "x" | "y" | "width" | "height"> {
  const [a0, a1] = [Math.min(...along), Math.max(...along)];
  const [c0, c1] = across;
  return orientation === "vertical"
    ? { x: c0, y: a0, width: c1 - c0, height: a1 - a0 }
    : { x: a0, y: c0, width: a1 - a0, height: c1 - c0 };
}

/**
 * Every bar's rectangle, in viewBox units. Grouped bars split a category's slot into
 * one thinner bar per series; stacked bars put the series end to end in one bar.
 */
export function layoutBars({
  rows,
  mode,
  orientation,
  valueScale,
  band,
  maxThickness,
  groupGap = 2,
}: BarLayoutInput): BarRect[] {
  const series = Math.max(1, rows[0]?.length ?? 1);
  const bars: BarRect[] = [];
  const groupWidth =
    mode === "stacked"
      ? Math.min(band.bandwidth, maxThickness)
      : Math.min(
          band.bandwidth,
          maxThickness * series + groupGap * (series - 1)
        );
  for (const [row, values] of rows.entries()) {
    const groupStart = band.center(row) - groupWidth / 2;
    if (mode === "stacked") {
      for (const segment of stackRow(values)) {
        bars.push({
          row,
          series: segment.series,
          value: segment.value,
          ...placeBar(
            orientation,
            [valueScale(segment.start), valueScale(segment.end)],
            [groupStart, groupStart + groupWidth]
          ),
        });
      }
      continue;
    }
    const thickness = (groupWidth - groupGap * (series - 1)) / series;
    for (const [index, value] of values.entries()) {
      if (value === null) {
        continue;
      }
      const start = groupStart + index * (thickness + groupGap);
      bars.push({
        row,
        series: index,
        value,
        ...placeBar(
          orientation,
          [valueScale(0), valueScale(value)],
          [start, start + thickness]
        ),
      });
    }
  }
  return bars;
}
